import React, { useEffect, useState } from "react";
import { Search, Check, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Avatar } from "./AvatarStack";

const inp = "w-full h-10 px-3 rounded-lg border border-neutral-200 focus:border-brand focus:outline-none text-sm bg-white disabled:bg-slate-50 disabled:text-slate-400";
const lbl = "font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1 block";
const empty = { pct: 100, contract: "", start: "", end: "", permanent: false };

export function AddMembersSheet({ project, employees, assignedIds, load, onClose, onDone }) {
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel(new Set()); setQ(""); setF(empty); }, [project]);

  const s = q.trim().toLowerCase();
  const avail = employees.filter((e) => e.is_active !== false && !assignedIds.has(e.id) &&
    (!s || (e.name || "").toLowerCase().includes(s) || (e.job_title || "").toLowerCase().includes(s)));
  const allOn = avail.length > 0 && avail.every((e) => sel.has(e.id));
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function submit() {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/assignments/bulk", {
        project_id: project.id, employee_ids: [...sel], all_employees: false,
        allocated_hours: parseFloat(f.pct) || 0, contract_hours_month: f.contract ? parseFloat(f.contract) : null,
        start_date: f.permanent ? null : f.start || null, end_date: f.permanent ? null : f.end || null, permanent: f.permanent,
      });
      toast.success(`${data.created} ${data.created === 1 ? "angajat alocat" : "angajați alocați"}${data.skipped ? `, ${data.skipped} deja alocați` : ""}`);
      await onDone();
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <Sheet open={!!project} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col" data-testid={`team-grid-${project?.code}`}>
        <SheetHeader className="p-6 border-b border-neutral-100 text-left">
          <SheetTitle className="font-display text-lg">Adaugă membri în echipă</SheetTitle>
          <SheetDescription>{project?.code} · {project?.name}</SheetDescription>
        </SheetHeader>
        <div className="px-6 pt-4 flex items-center gap-3">
          <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-lg border border-neutral-200 bg-white focus-within:border-brand">
            <Search className="h-4 w-4 text-neutral-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută după nume sau funcție..." data-testid="team-search"
              style={{ boxShadow: "none" }} className="flex-1 min-w-0 bg-transparent outline-none border-0 text-sm placeholder:text-neutral-400" />
          </div>
          <button type="button" data-testid="team-select-all" disabled={!avail.length}
            onClick={() => setSel(allOn ? new Set() : new Set(avail.map((e) => e.id)))}
            className="text-[13px] text-brand font-medium hover:underline disabled:opacity-40 shrink-0">{allOn ? "Deselectează" : "Selectează toți"}</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-1">
          {avail.length === 0 && <div className="py-10 text-center text-sm text-ink-muted">{s ? "Niciun angajat nu corespunde căutării." : "Toți angajații activi sunt deja în echipă."}</div>}
          {avail.map((e) => {
            const on = sel.has(e.id);
            const l = load[e.id] || 0;
            return (
              <button key={e.id} type="button" onClick={() => toggle(e.id)} data-testid={`team-emp-${e.id}`}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${on ? "border-brand bg-brand/[0.05]" : "border-transparent hover:bg-slate-50"}`}>
                <span className={`shrink-0 h-5 w-5 rounded border flex items-center justify-center ${on ? "bg-brand border-brand" : "border-neutral-300 bg-white"}`}>
                  {on && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                </span>
                <Avatar id={e.id} name={e.name} size={32} ring={false} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-ink truncate">{e.name}</span>
                  <span className="block text-[12px] text-ink-muted truncate">{e.job_title || e.role}</span>
                </span>
                <span className={`text-[11px] tabular-nums shrink-0 ${l >= 100 ? "text-red-600 font-semibold" : "text-ink-muted"}`} title="Încărcare luna aceasta pe toate proiectele">{l}%</span>
              </button>
            );
          })}
        </div>
        <div className="border-t border-neutral-100 p-6 space-y-3 bg-slate-50/60">
          <div className="grid grid-cols-2 gap-3">
            <div><span className={lbl}>Alocat (%) / persoană</span><input type="number" min="0" max="100" step="5" value={f.pct} onChange={(e) => setF({ ...f, pct: e.target.value })} className={`${inp} font-mono`} data-testid="team-hours" /></div>
            <div><span className={lbl}>Ore contract / lună</span><input type="number" step="8" placeholder="—" value={f.contract} onChange={(e) => setF({ ...f, contract: e.target.value })} className={`${inp} font-mono`} data-testid="team-contract-hours" /></div>
            <div><span className={lbl}>De la</span><input type="date" value={f.start} disabled={f.permanent} onChange={(e) => setF({ ...f, start: e.target.value })} className={inp} data-testid="team-start" /></div>
            <div><span className={lbl}>Până la</span><input type="date" value={f.end} disabled={f.permanent} onChange={(e) => setF({ ...f, end: e.target.value })} className={inp} data-testid="team-end" /></div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.permanent} onChange={(e) => setF({ ...f, permanent: e.target.checked })} data-testid="team-permanent" /> Permanent</label>
            <button data-testid="team-confirm" onClick={submit} disabled={busy || sel.size === 0}
              className="ml-auto h-10 px-4 rounded-lg bg-brand text-white text-sm font-medium flex items-center gap-2 hover:bg-[#2f5290] disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Alocă {sel.size || ""}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
